/**
 * ControlPanel API (`/ssml-tuning`) クライアント — AI 解析 / サンプル生成 / 利用ログ。
 *
 * 音声チューニング画面 (`/ssml-tuning`) が使う 3 本の API を叩く。
 *
 *   - Step1: `POST /ssml-tuning/analyze` … 改善案 / 改善理由 (計画 8.3.2)
 *   - Step3: `POST /ssml-tuning/samples` … SSML 候補 (最大 5 件) (計画 8.3.3)
 *   - 利用ログ: `POST /ssml-tuning/logs` … Readme 改善の材料収集 (JSONL 1 行)
 *
 * ## 設計上の約束
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する
 *   (既存 `PhraseTemplatesClient` と同じ流儀)。
 * - **画面は AI (Bedrock) を知らない**。プロンプトもモデル選択もサーバ側にあり、
 *   フロントは「テキストを送ると改善案が返る」以上の知識を持たない。
 *   これにより「画面ごとに違うモデルを使う」事故が構造的に起こらない。
 * - 応答の **未知プロパティは無視** する。将来サーバ側が項目を増やしても
 *   (計画 8.7.2 の互換方針) 画面が壊れないようにするため、型は必須項目のみ定義する。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type { ApiErrorBody } from './types';

/** `POST /ssml-tuning/analyze` のリクエスト。 */
export interface SsmlTuningAnalyzeRequest {
  /** 放送テキスト (必須) */
  text: string;
  /**
   * 読み上げ要望 (任意)。
   *
   * 例: 「駅名を強調したい」「申し訳ない感じに」「高齢者向けにゆっくり」。
   * 空の場合はサーバ側で「要望なし」として扱われる (AI に推測させない)。
   */
  request_note?: string;
}

/**
 * `POST /ssml-tuning/analyze` の応答。
 *
 * ⚠️ 必須項目のみを定義する。サーバが将来項目を追加しても
 * 型エラーにならないようにするため (計画 8.7.2)。
 */
export interface SsmlTuningAnalyzeResponse {
  /**
   * AI へ渡した放送文。
   *
   * 「何を元に直したか」を画面で固定するために表示する。
   * サーバが受け取った値をそのまま返すため、入力欄の内容と必ず一致する。
   */
  input_text: string;
  /** AI 改善案 (画面で **編集可能**。後続 Step の入力になる) */
  improved_text: string;
  /** 改善理由 (読み取り専用) */
  reason: string;
  /** 使用した AI モデル ID (安価モデルで動いたことの確認用) */
  model_id: string;
}

/** `POST /ssml-tuning/samples` のリクエスト。 */
export interface SsmlTuningSamplesRequest {
  /**
   * SSML 化する放送文 (必須)。
   *
   * ⚠️ Step2 で利用者が **編集した後の改善案** を送る。AI 応答をそのまま
   * 送ると「編集したのに反映されない」不具合になる (計画 8.4.2 の約束)。
   */
  text: string;
  /** 読み上げ要望 (任意 / Step1 と同じ要望を再送する) */
  request_note?: string;
}

/**
 * SSML 候補 1 件。
 *
 * 画面の候補カードが表示する 3 項目に対応する (計画 8.3.3)。
 */
export interface SsmlSampleCandidate {
  /**
   * Audio タイトル。
   *
   * ⚠️ **AI が決める** (固定リストではない)。例: 駅名強調 / 申し訳ない感じ /
   * 高齢者向け。ダウンロード時のファイル名にも使うため、サーバ側で
   * 空欄補完と重複解消が済んでいる。
   */
  title: string;
  /** SSML 全文 (`<speak>` で始まり `</speak>` で終わる) */
  ssml: string;
  /** AI コメント (何を狙った SSML かの説明) */
  comment: string;
}

/**
 * `POST /ssml-tuning/samples` の応答。
 *
 * ⚠️ 必須項目のみを定義する (計画 8.7.2 の互換方針)。
 * Phase2 で `readme_added` が増えても型エラーにしない。
 */
export interface SsmlTuningSamplesResponse {
  /**
   * SSML 候補の一覧。
   *
   * ⚠️ **5 件に満たない場合がある**。AI 応答が出力トークン上限で切れると
   * 候補が減るため、サーバは取れた分だけ返す (計画リスク R5)。
   * 画面は「5 件前提」で固定長の配列アクセスをしてはいけない。
   */
  candidates: SsmlSampleCandidate[];
  /** 使用した AI モデル ID (安価モデルで動いたことの確認用) */
  model_id: string;
}

/**
 * 利用ログの記録イベント種別 (`POST /ssml-tuning/logs` の `eventType`)。
 *
 * ⚠️ サーバ側 (`lambda/common/ssml_tuning_log.py` の `EVENT_TYPES`) と
 * **完全に一致させる**。値がずれると 400 で弾かれ、記録が静かに欠ける
 * (画面は 204 を期待して無視するので気づけない)。
 *
 *   - `candidate_generated` … AI が SSML 候補を作った (改善パターンの母集団)
 *   - `audio_recreated`     … SSML を編集して音声を作り直した
 *                             (**Readme 改善の最重要シグナル**)
 *   - `audio_downloaded`    … 音声を保存した (= 実際に使えた候補の指標)
 */
export type SsmlTuningLogEventType = 'candidate_generated' | 'audio_recreated' | 'audio_downloaded';

/**
 * `POST /ssml-tuning/logs` のリクエスト (利用ログ 1 件)。
 *
 * ## ⚠️ 利用者を識別する項目を入れてはいけない
 *
 * 本ログの目的は **Readme 改善の材料収集** であって利用者の監視ではない。
 * Cognito sub / メール / IP のような識別子はサーバ側の allowlist で
 * 落とされるが、そもそも画面から送らないこと (送る実装があると
 * 「後で使えるように残しておこう」という運用に流れる)。
 *
 * ## ⚠️ `timestamp` は送らない
 *
 * 記録時刻はサーバが採番する。端末の時計を信用すると時刻ずれで並び順が
 * 壊れ、「最近 1000 件」という取り出し方が意味を失う。
 */
export interface SsmlTuningLogRequest {
  /** 記録イベント種別 (必須) */
  eventType: SsmlTuningLogEventType;
  /** Step1 の放送テキスト (任意) */
  broadcastText?: string;
  /** Step1 の読み上げ要望 (任意) */
  requestText?: string;
  /** 候補タイトル (任意 / AI が決めた見出し) */
  candidateTitle?: string;
  /** AI が生成した SSML (任意) */
  generatedSsml?: string;
  /** 利用者が編集した SSML (任意) */
  editedSsml?: string;
  /** 音声を作り直したか (任意) */
  audioRecreated?: boolean;
  /** 音声を保存したか (任意) */
  audioDownloaded?: boolean;
}

export interface SsmlTuningClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class SsmlTuningClient {
  private readonly options: SsmlTuningClientOptions;

  constructor(options: SsmlTuningClientOptions) {
    this.options = options;
  }

  /**
   * POST /ssml-tuning/analyze (放送文の AI 解析)
   *
   * @param params 放送テキストと読み上げ要望 (任意)
   * @returns 入力文 / AI 改善案 / 改善理由 / モデル ID
   * @throws ApiError 400 (入力不正) / 429 (混雑) / 502 (AI 応答不正) / 503 (未有効化)
   *
   * ⚠️ **押した時だけ課金される**。画面側で自動実行 (onChange 等) しないこと。
   */
  async analyze(params: {
    text: string;
    requestNote?: string;
  }): Promise<SsmlTuningAnalyzeResponse> {
    const payload: SsmlTuningAnalyzeRequest = { text: params.text };
    // 空文字は送らない。サーバは未指定と空文字を同じ「要望なし」として扱うが、
    // 送らないことで「要望を入れていない」ことがリクエストログでも明確になる。
    if (typeof params.requestNote === 'string' && params.requestNote.trim() !== '') {
      payload.request_note = params.requestNote;
    }
    return this.request<SsmlTuningAnalyzeResponse>('POST', 'ssml-tuning/analyze', payload);
  }

  /**
   * POST /ssml-tuning/samples (SSML 候補 5 件の生成)
   *
   * @param params SSML 化する放送文 (Step2 の編集後テキスト) と読み上げ要望 (任意)
   * @returns 候補一覧 (最大 5 件) とモデル ID
   * @throws ApiError 400 (入力不正) / 429 (混雑) / 502 (AI 応答不正) / 503 (未有効化)
   *
   * ⚠️ **押した時だけ課金される**。画面側で自動実行しないこと。
   * ⚠️ 本 API は **音声を合成しない**。画面が候補ごとに
   *    `POST /audio-preview` を呼んで合成する (計画 9.3.1)。
   *    「Audio サンプル作成」1 回で候補生成に続けて全候補を合成するため、
   *    Polly の呼び出しは **候補数と同じ回数** (最大 5 回) 発生する。
   */
  async createSamples(params: {
    text: string;
    requestNote?: string;
  }): Promise<SsmlTuningSamplesResponse> {
    const payload: SsmlTuningSamplesRequest = { text: params.text };
    // analyze と同じ扱い: 空の要望は送らない (ログで「未入力」が判別できる)。
    if (typeof params.requestNote === 'string' && params.requestNote.trim() !== '') {
      payload.request_note = params.requestNote;
    }
    return this.request<SsmlTuningSamplesResponse>('POST', 'ssml-tuning/samples', payload);
  }

  /**
   * POST /ssml-tuning/logs (利用ログの記録)
   *
   * 「どんな放送文にどんな要望を出し、AI が出した SSML をどう直したか」を
   * CloudWatch Logs へ 1 行 1 JSON で残す。目的は **Readme 改善の材料収集**
   * であり、利用者の監視ではない (識別子は送らない / サーバも記録しない)。
   *
   * @param entry 記録する 1 件 (`eventType` 必須 / 他は任意)
   *
   * ## ⚠️ 例外を投げない (fire-and-forget)
   *
   * ログの失敗で利用者の操作 (音声の再作成・ダウンロード) を止めては
   * いけない。本ログは業務データではなく改善材料なので、取れなかった記録は
   * **諦める方が正しい**。そのため戻り値も返さず、失敗は握り潰す。
   *
   * ⚠️ 呼び出し側で `await` する必要はない (`void client.recordLog(...)`)。
   * ダウンロードのように「押した瞬間にブラウザが遷移する」操作では
   * await するとログ送信が中断され得るが、それも許容する
   * (記録漏れは分析時の件数が少し減るだけで、操作は必ず成功する)。
   *
   * ## ⚠️ 応答は 204 (No Content) なので body を読まない
   *
   * `request()` は `res.json()` を呼ぶため 204 では例外になる。
   * ログ用途では応答値を使わないので、専用の送信処理を持つ。
   */
  async recordLog(entry: SsmlTuningLogRequest): Promise<void> {
    try {
      const url = joinUrl(this.options.config.apiUrl, 'ssml-tuning/logs');
      const idToken = await this.options.getIdToken();
      const fetchImpl = this.options.fetchImpl ?? fetch;

      await fetchImpl(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(entry),
      });
      /*
       * ⚠️ `res.ok` を確認して throw しない。
       * 400 (eventType のタイプミス等) はコードの不具合だが、それを
       * 画面のエラー表示にすると「音声は作れたのにエラーが出る」体験になる。
       * 実装ミスはサーバ側の警告ログ (`invalid eventType`) で見つける。
       */
    } catch {
      /*
       * ネットワーク断・認証切れ等。ここで握り潰すのが本メソッドの要点。
       * ⚠️ `console.error` も出さない。ダウンロードのたびに赤いログが出ると
       * 「壊れている」と誤解され、本当の不具合の調査を妨げる。
       */
    }
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      // サーバのメッセージ (「モデルアクセスを有効化してください」等) を
      // そのまま画面へ出せるようにする。運用者が次の行動を判断できるため。
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}
